import type {
  Certification,
  Education,
  JobSearchStatus,
  Language,
  Project,
  ResumeSource,
  WorkExperience,
} from '../../types/userProfile';

// ──────────────────────────────────────────────────────────────────────────────
// OnboardingDraft — the in-memory shape used by every step.
//
// Every field is a string (or string[]) at the form layer to keep validation
// uniform. The wizard converts numeric fields to `number | null` immediately
// before calling `useUserProfile().save(...)`.
// ──────────────────────────────────────────────────────────────────────────────

export interface OnboardingDraft {
  // Step 1 — Identity
  displayName:    string;
  /** Professional headline — optional in onboarding validation; surfaced on Profile completeness. */
  headline:       string;
  currentRole:    string;
  experienceYears: string;          // string at form layer; coerced on save

  // Step 2 — Skills
  skills:         string[];

  // Step 3 — Preferences
  industry:           string | null;
  jobSearchStatus:    JobSearchStatus | null;
  preferredJobTypes:  string[];
  location:           string;
  salaryExpectationInr: string;     // string at form layer

  // Step 4 — Links
  linkedinUrl:  string;
  githubUrl:    string;
  portfolioUrl: string;

  // Step 5 — Resume Data (optional, can be populated via file upload)
  professionalSummary: string;
  experiences:        WorkExperience[];
  education:          Education[];
  projects:           Project[];
  certifications:     Certification[];
  languages:          Language[];
  resumeSource:       ResumeSource | null;
  resumeFileUrl:      string | null;
  resumeFileName:     string | null;
  resumeRawText:      string | null;
  resumeUpdatedAt:    string | null;
}

/** Per-field error map for the current step (or accumulated across steps). */
export type OnboardingErrors = Partial<Record<keyof OnboardingDraft, string>>;

/** Default empty draft used when there is no existing user_profiles row. */
export const EMPTY_DRAFT: OnboardingDraft = {
  displayName:           '',
  headline:              '',
  currentRole:           '',
  experienceYears:       '',
  skills:                [],
  industry:              null,
  jobSearchStatus:       null,
  preferredJobTypes:     [],
  location:              '',
  salaryExpectationInr:  '',
  linkedinUrl:           '',
  githubUrl:             '',
  portfolioUrl:          '',
  // Resume data
  professionalSummary:   '',
  experiences:           [],
  education:             [],
  projects:              [],
  certifications:        [],
  languages:             [],
  resumeSource:          null,
  resumeFileUrl:         null,
  resumeFileName:        null,
  resumeRawText:         null,
  resumeUpdatedAt:       null,
};
